import mvtArticle from '@/common/components/mvtarticle/mvt-article.vue';
import mvtFaq from '@/common/components/mvtfaq/mvt-faq.vue';
import mvtInputDecimalNumber from '@/common/components/mvtinputdecimalnumber/mvt-inputdecimalnumber.vue';
import mvtInputNumber from '@/common/components/mvtinputnumber/mvt-inputnumber.vue';
import mvtMortgage from '@/common/components/mvtmortgage/mvt-mortgage.vue';
import mvtSelect from '@/common/components/mvtselect/mvt-select.vue';
import faqConf from '@/common/store/glb/data.faqs';
import mapper from '@/common/store/mapper/index.js';
import sort from '@/common/utilities/sort';
import mortgageTools from '@/staticpages/components/common/mortgagetools/mortgagetools.vue';
import { defineAsyncComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { getPathBySearchObj } from '../../../../widget/spec/searchHelper';
import getStateByZipCode from '../../../common/utilities/statesByZipcode';
import mvtDialogCenter from '@/common/components/mvtdialogcenter/mvt-dialogcenter.vue';
import mvtLazyload from '@/common/components/mvtlazyload/mvt-lazyload.vue';
import mvtHeader from '@/common/components/mvtheader/mvt-header.vue';
import mvtFooter from '@/common/components/mvtfooter/mvt-footer.vue';
import { parseQueryString } from '@/common/utilities/nojq/_utilities.js';
import { handleMortgagePageCta } from '@/common/utilities/mortgagePageCta.js';

const mvtRateTrend = defineAsyncComponent(() => import(/* webpackChunkName: "rateTrend" */ '@/common/components/mvtratetrend/mvt-ratetrend.vue'));

export default {
    name: 'pageMortgagecalculator',
    inject: ['$eventBus'],
    components: {
        mvtDialogCenter,
        mvtInputNumber,
        mvtMortgage,
        mortgageTools,
        mvtArticle,
        mvtInputDecimalNumber,
        mvtRateTrend,
        mvtSelect,
        mvtFaq,
        mvtLazyload,
        mvtHeader,
        mvtFooter,
    },
    data() {
        return {
            tag: null,
            faqQuery: null,
            showExtraFields: false,
            zipcode: '',
            state: '',
            downPayment: 0,
            percentDown: 25,
            homePrice: 500000,
            interestRate: 0,
            loanType: 0,
            homeInsurance: 0,
            homeInsurancePerMonth: 0,
            hoafee: 0,
            hoaPerMonth: 0,
            hasHOADues: true,
            hasPropertyTax: true,
            pmiPerMonth: 0,
            taxPerMonth: 0,
            propertyTax: 0,
            loanPerMonth: 0,
            monthTotal: 0,
            taxRateByState: {
                AL: 0.43,
                AK: 1.21,
                AS: 0, //American Samoa
                AZ: 0.84,
                AR: 0.62,
                CA: 0.81,
                CO: 0.62,
                CT: 1.91,
                DE: 0.53,
                DC: 0.57,
                FM: 0, //Federated States Of Micronesia
                FL: 1.1,
                GA: 0.96,
                GU: 0, //Guam
                HI: 0.28,
                ID: 0.77,
                IL: 2.25,
                IN: 0.88,
                IA: 1.47,
                KS: 1.41,
                KY: 0.84,
                LA: 0.48,
                ME: 1.27,
                MH: 0, //Marshall Islands
                MD: 1.08,
                MA: 1.18,
                MI: 1.83,
                MN: 1.19,
                MS: 0.78,
                MO: 1.0,
                MT: 0.87,
                NE: 1.88,
                NV: 0.92,
                NH: 2.1,
                NJ: 2.29,
                NM: 0.72,
                NY: 1.58,
                NC: 0.85,
                ND: 1.22,
                MP: 0, //Northern Mariana Islands,
                OH: 1.55,
                OK: 0.87,
                OR: 1.08,
                PW: 0, //Palau
                PA: 1.51,
                PR: 0, //Puerto Rico
                RI: 1.61,
                SC: 0.57,
                SD: 1.36,
                TN: 0.75,
                TX: 1.93,
                UT: 0.69,
                VT: 1.72,
                VI: 0, //Virgin Islands
                VA: 0.8,
                WA: 1.08,
                WV: 0.59,
                WI: 1.97,
                WY: 0.62,
            },
            interestRateList: [],
            creditList: [],
            credit: 760,
        };
    },
    computed: {
        ...mapGetters('glb', ['viewport', 'glb', 'getMortgageOptionsData', 'getSplit']),
        pricePerMonth() {
            return this.homeInsurancePerMonth + this.pmiPerMonth + this.hoaPerMonth + this.taxPerMonth + this.loanPerMonth;
        },
        interestOption() {
            return this.interestRateList[this.loanType];
        },
        chartData() {
            var data = [];
            if (this.loanPerMonth > 0) {
                data.push({
                    key: 'Interest',
                    className: 'interest',
                    price: this.loanPerMonth,
                    value: Math.ceil((this.loanPerMonth / this.pricePerMonth) * 100) + '%',
                });
            }
            if (this.taxPerMonth > 0) {
                data.push({
                    key: 'Taxes',
                    className: 'tax',
                    price: this.taxPerMonth,
                    value: ((this.taxPerMonth / this.pricePerMonth) * 100).toFixed(0) + '%',
                });
            }
            if (this.hoaPerMonth > 0) {
                data.push({
                    key: 'HOA',
                    className: 'hoa',
                    price: this.hoaPerMonth,
                    value: ((this.hoaPerMonth / this.pricePerMonth) * 100).toFixed(0) + '%',
                });
            }
            if (this.pmiPerMonth > 0) {
                data.push({
                    key: 'PMI',
                    className: 'pmi',
                    price: this.pmiPerMonth,
                    value: ((this.pmiPerMonth / this.pricePerMonth) * 100).toFixed(0) + '%',
                });
            }
            if (this.homeInsurancePerMonth > 0) {
                data.push({
                    key: 'Insurance',
                    className: 'insurance',
                    price: this.homeInsurancePerMonth,
                    value: ((this.homeInsurancePerMonth / this.pricePerMonth) * 100).toFixed(0) + '%',
                });
            }
            return data;
        },
    },
    methods: {
        ...mapActions('glb', ['fetchAllZipListings', 'getGeoInfoBySearch', 'fetchCachedMortgageRates']),
        getCurrentZipMortgage() {
            if (this.zipcode.length === 5) {
                this.state = getStateByZipCode(this.zipcode);
                this.getUrl(this.state, this.zipcode);
            }
        },
        getMortgageBaseOptions() {
            if (this.getMortgageOptionsData) {
                this.creditList = this.getMortgageOptionsData.creditList || [];
                this.interestRateList = this.getMortgageOptionsData.interestRateList || [];
            }
            this.interestRate = this.interestOption ? this.interestOption.interestRate : 0;
            const queryObj = parseQueryString();
            if (queryObj && queryObj.zipcode) {
                this.zipcode = queryObj.zipcode;
                this.state = queryObj.state;
            } else {
                this.zipcode = this.glb.geo.zipcode || '94403';
                this.state = this.glb.geo.state || 'CA';
            }
            this.getUrl(this.state, this.zipcode);
        },
        getUrl(stateCode, zipcode) {
            let path = getPathBySearchObj({ zipcode: zipcode, stateCode: stateCode });
            this.getHouseListings(path);
        },
        getHouseListings(urlPath) {
            this.fetchAllZipListings({ zipcodePath: urlPath }).then((res) => {
                if (res && res.data) {
                    const result = res.data.map((listing) => {
                        return mapper.property(listing, this.glb);
                    });
                    const listings = sort.sortProperty('price_asc', result);
                    this.homePrice = listings[4] && listings[5] ? Math.round((listings[4].price + listings[5].price) / 2) : '500000';
                    this.mortgageCalculations();
                }
            });
        },
        updateInterstRate() {
            this.interestRate = this.interestOption ? this.interestOption.interestRate : 0;
            this.mortgageCalculations();
        },
        updataDownpayment() {
            if (this.percentDown > 100) {
                this.percentDown = 100;
            } else if (this.percentDown < 0) {
                this.percentDown = 0;
            }
            this.downPayment = Math.round((this.homePrice * this.percentDown) / 100);
            this.mortgageCalculations();
        },
        updataTaxRate() {
            if (this.propertyTaxRate > 100) {
                this.propertyTaxRate = 100;
            } else if (this.propertyTaxRate < 0) {
                this.propertyTaxRate = 0;
            }
            this.propertyTax = Math.round((this.homePrice * this.propertyTaxRate) / 100);
            this.mortgageCalculations();
        },
        updataPercentageDown() {
            this.percentDown = this.round((this.downPayment / this.homePrice) * 100, 2);
            if (this.percentDown > 100) {
                this.percentDown = 100;
            } else if (this.percentDown < 0) {
                this.percentDown = 0;
            }
            this.mortgageCalculations();
        },
        mortgageCalculations() {
            this.downPayment = Math.round((this.homePrice * this.percentDown) / 100);
            this.homeInsurance = Math.round((this.homePrice / 2000) * 3.5);
            this.homeInsurancePerMonth = Math.round(this.homeInsurance / 12);
            this.percentDown = this.round((this.downPayment / this.homePrice) * 100, 2);
            this.propertyTaxRate = this.taxRateByState[this.state] || 0;
            this.propertyTax = Math.round((this.homePrice * this.propertyTaxRate) / 100);
            this.propertyTaxRate = this.round((this.propertyTax / this.homePrice) * 100, 2);

            var propertyTaxRate = this.hasPropertyTax ? this.propertyTaxRate / 100 || 0 : 0;
            var loanYear = this.interestOption ? this.interestOption.loanYear : 0;
            var loanMonth = loanYear * 12;
            var interestMonthRate = this.interestRate / 100 / 12;
            var loanAmount = this.homePrice - this.downPayment;
            var loanRate = loanAmount / this.homePrice;
            var PMI = this.percentDown >= 20 ? 0 : this.calPMI(loanRate, loanYear);
            var pmiAnnualFee = PMI * loanAmount;
            var propertyHoafee = this.hasHOADues ? this.hoafee || 0 : 0;
            var taxAnnualAmount = propertyTaxRate * this.homePrice;
            var _l = Math.pow(1 + interestMonthRate, loanMonth);

            this.loanPerMonth = Math.round((loanAmount * (interestMonthRate * _l)) / (_l - 1));
            this.pmiPerMonth = Math.round(pmiAnnualFee / 12);
            this.hoaPerMonth = Math.round(propertyHoafee);
            this.taxPerMonth = Math.round(taxAnnualAmount / 12);
            this.monthTotal = this.loanPerMonth + this.taxPerMonth + this.hoaPerMonth + this.homeInsurancePerMonth + this.pmiPerMonth;
        },
        calPMI(loanRate, loanYear) {
            if (loanRate >= 0.9001 && loanRate <= 1 && loanYear === 30) {
                return 0.0062;
            }
            if (loanRate >= 0.9001 && loanRate <= 1 && loanYear === 20) {
                return 0.005867;
            }
            if (loanRate >= 0.9001 && loanRate <= 1 && loanYear === 15) {
                return 0.0057;
            }
            if (loanRate >= 0.8501 && loanRate <= 0.9 && loanYear === 30) {
                return 0.0044;
            }
            if (loanRate >= 0.8501 && loanRate <= 0.9 && loanYear === 20) {
                return 0.004067;
            }
            if (loanRate >= 0.8501 && loanRate <= 0.9 && loanYear === 15) {
                return 0.0039;
            }
            if (loanRate >= 0.8 && loanRate <= 0.85 && loanYear === 30) {
                return 0.0027;
            }
            if (loanRate >= 0.8 && loanRate <= 0.85 && loanYear === 20) {
                return 0.002367;
            }
            if (loanRate >= 0.8 && loanRate <= 0.85 && loanYear === 15) {
                return 0.0022;
            }
            return 0;
        },
        round(value, decimals) {
            return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
        },
        preApprovalPage(evt) {
            handleMortgagePageCta({
                getSplit: this.getSplit,
                glb: this.glb,
                zipcode: this.zipcode,
                utmContent: 'get_started',
                ctaLabel: 'Get Started',
                evt,
                eventBus: this.$eventBus,
                trigger: this.$options.name,
            });
        },
    },
    beforeMount() {
        this.fetchCachedMortgageRates().then((res) => {
            if (res && res.data) {
                const mortgagePerioMapping = {
                    PERIOD_FIXED_30YEARS: 'FRM-30',
                    PERIOD_FIXED_15YEARS: 'FRM-15',
                    PERIOD_ARM_5YEARS: 'ARM-51',
                };
                let interestRate = this.getMortgageOptionsData.interestRateList;
                for (const key in mortgagePerioMapping) {
                    let item = interestRate.find((e) => e.period === key);
                    let latestValue = res.data[mortgagePerioMapping[key]];
                    if (latestValue && item.interestRate !== latestValue) {
                        item.interestRate = latestValue;
                    }
                }
            }
            this.updateInterstRate();
        });
    },
    mounted() {
        this.tag = this.glb.articleConfig.tag.mortgageCalculator + '';
        this.faqQuery = faqConf.mortgageCalculator;
        this.getMortgageBaseOptions();
        this.$eventBus.$emit('eventbus-pageLoaded');
    },
};
